import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { getMyPurchaseOrder, acknowledgeMyPurchaseOrder } from "@/lib/vendor-portal.functions";
import { formatDate } from "@/features/assets/format";


export const Route = createFileRoute("/vendor/purchase-orders/$poId")({
  component: PODetail,
});

function PODetail() {
  const { poId } = Route.useParams() as { poId: string };
  const navigate = useNavigate();
  const qc = useQueryClient();
  const fn = useServerFn(getMyPurchaseOrder);
  const ack = useServerFn(acknowledgeMyPurchaseOrder);

  const { data, isLoading } = useQuery({
    queryKey: ["vendor", "po", poId],
    queryFn: () => fn({ data: { po_id: poId } }),
  });

  const ackM = useMutation({
    mutationFn: () => ack({ data: { po_id: poId } }),
    onSuccess: () => {
      toast.success("PO acknowledged");
      qc.invalidateQueries({ queryKey: ["vendor"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading) return <p>Loading…</p>;
  if (!data?.po) return <p>PO not found.</p>;
  const po = data.po as any;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{po.po_number}</h1>
          <p className="text-sm text-muted-foreground">Date: {po.po_date} · Expected: {po.expected_delivery_date ?? "—"}</p>
          <div className="mt-2 flex gap-2">
            <Badge variant="outline">{po.status}</Badge>
            {po.acknowledged_at ? <Badge>Acknowledged {formatDate(po.acknowledged_at)}</Badge> : <Badge variant="outline" className="border-amber-400 text-amber-700">Pending acknowledgement</Badge>}
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate({ to: ("/vendor/purchase-orders" as any) })}>Back</Button>
          {!po.acknowledged_at && (
            <Button onClick={() => ackM.mutate()} disabled={ackM.isPending}>
              {ackM.isPending ? "Acknowledging…" : "Acknowledge PO"}
            </Button>
          )}
        </div>
      </div>

      <div className="rounded-md border bg-card">
        <table className="w-full text-sm">
          <thead className="border-b bg-muted/40 text-xs uppercase text-muted-foreground">
            <tr>
              <th className="px-3 py-2 text-left">#</th>
              <th className="px-3 py-2 text-left">Item</th>
              <th className="px-3 py-2 text-right">Qty</th>
              <th className="px-3 py-2 text-right">Unit ₹</th>
              <th className="px-3 py-2 text-right">Tax %</th>
              <th className="px-3 py-2 text-right">Line Total ₹</th>
              <th className="px-3 py-2 text-right">Received</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((it: any, idx: number) => (
              <tr key={it.id} className="border-b last:border-0">
                <td className="px-3 py-2">{idx + 1}</td>
                <td className="px-3 py-2">
                  <div className="font-medium">{it.item_name}</div>
                  {it.item_description && <div className="text-xs text-muted-foreground">{it.item_description}</div>}
                </td>
                <td className="px-3 py-2 text-right">{it.quantity_ordered}</td>
                <td className="px-3 py-2 text-right">{Number(it.unit_price).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{Number(it.tax_percentage ?? 0)}</td>
                <td className="px-3 py-2 text-right">{Number(it.line_total).toLocaleString()}</td>
                <td className="px-3 py-2 text-right">{Number(it.received_quantity ?? 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot className="bg-muted/40">
            <tr>
              <td colSpan={5} className="px-3 py-2 text-right font-semibold">Total</td>
              <td className="px-3 py-2 text-right font-semibold">₹{Number(po.total_amount ?? 0).toLocaleString()}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      </div>

      {po.terms_and_conditions && (
        <div className="rounded-md border bg-card p-4">
          <h3 className="mb-2 text-sm font-semibold">Terms & Conditions</h3>
          <p className="whitespace-pre-wrap text-sm text-muted-foreground">{po.terms_and_conditions}</p>
        </div>
      )}
    </div>
  );
}
